/**
 * FeatureShowcase Component
 * 
 * Two-panel feature showcase section that follows the hero.
 * Panels: AANA Workspace → Content & Courses
 */

import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import FeaturePanel from './FeaturePanel';
import ImageCarousel from './ImageCarousel';
import './FeatureShowcase.css';

// Import section 2 collage
import collageWebp from './assets/section2/collage.webp';
import collageFallback from './assets/section2/collage.jpg';
import loopVideo from './assets/15loop.mp4';

// Collage visual component for Section 2
const CollageVisual = () => (
  <div className="collage-visual">
    <picture>
      <source srcSet={collageWebp} type="image/webp" />
      <img
        src={collageFallback}
        alt="Platform overview showing courses, units, goals, network and content management features"
        className="collage-visual__image"
      />
    </picture>
  </div>
);

const LoopVideoVisual = () => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [loadFullVideo, setLoadFullVideo] = useState(false);
  const [isDeepLinkDemo, setIsDeepLinkDemo] = useState(false);
  const fullVideoSrc = `${process.env.PUBLIC_URL}/full90s.mp4`;

  // Check URL on mount for auto-play demo parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const playParam = (urlParams.get('play') || '').toLowerCase();
    if (playParam.startsWith('demo') || window.location.hash === '#play-demo') {
      setIsDeepLinkDemo(true);
      setIsVisible(true);
      setLoadFullVideo(true);

      const scrollToVideo = () => {
        if (containerRef.current) {
          containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      };

      setTimeout(scrollToVideo, 200);
      setTimeout(scrollToVideo, 800);
      setTimeout(scrollToVideo, 1500);
    }
  }, []);

  useEffect(() => {
    if (isDeepLinkDemo) {
      setIsVisible(true);
      return undefined;
    }

    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.15, rootMargin: '180px 0px' }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [isDeepLinkDemo]);

  useEffect(() => {
    if (!loadFullVideo || !videoRef.current) return;
    const playPromise = videoRef.current.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }, [loadFullVideo]);

  return (
    <div className="loop-video-visual" ref={containerRef}>
      {isVisible ? (
        <>
          <video
            key={loadFullVideo ? 'full-video' : 'preview-video'}
            ref={videoRef}
            className="loop-video-visual__video"
            src={loadFullVideo ? fullVideoSrc : loopVideo}
            controls={loadFullVideo}
            autoPlay
            loop={!loadFullVideo}
            muted={isDeepLinkDemo || !loadFullVideo}
            playsInline
            preload={loadFullVideo ? 'metadata' : 'none'}
          />

          {!loadFullVideo && (
            <button
              type="button"
              className="loop-video-visual__play-btn"
              aria-label="Load and play full video"
              onClick={() => setLoadFullVideo(true)}
            >
              <span className="loop-video-visual__play-icon" aria-hidden="true">▶</span>
            </button>
          )}
        </>
      ) : (
        <div className="loop-video-visual__placeholder" aria-hidden="true" />
      )}
    </div>
  );
};

// Panel content data
const FEATURE_PANELS = [
  {
    id: 'workspace',
    eyebrow: 'AANA WORKSPACE',
    headline: "AI that's designed to elevate you, not replace you.",
    description: '',
    bullets: [
      'No prompting, steer your AI through visual intent and intuitive, sketch-based direction',
      'Draw, annotate and layer visuals on top of your own content',
      'Reuse your best explanations as scaffolds across sessions',
      'Seamless browser based workflow tailored for creative flexibility',
    ],
    tagline: 'Make your reasoning visible. Bridge the gap between what you know and what you deliver.',
    primaryCta: null,
    secondaryCta: null,
    visualPosition: 'right',
    visualCaption: {
      text: 'Screenshots from early development. The trial experience will feature a more refined UI, smoother interactions, and additional capabilities.',
      showCta: true,
      ctaLabel: 'Register Interest',
      ctaHref: '#early-access',
    },
  },
  {
    id: 'content',
    eyebrow: 'CONTENT MANAGEMENT AND NETWORKING',
    headline: "Give your work a home. Give your practice momentum.",
    description: '',
    bullets: [
      'Upload your materials, notes, links, assign tasks, save and reuse scaffolded diagrams',
      'Create clean course pages, lesson outlines, and module structures that make your teaching look established, intentional, and trustworthy',
      'Add learners, track their involvement, and share updates or session links — all without forcing anyone into a heavy enrolment system',
    ],
    primaryCta: null,
    secondaryCta: null,
    visualPosition: 'left',
    visualCaption: {
      text: 'Screenshots from early development. The trial experience will feature a more refined UI, smoother interactions, and additional capabilities.',
      showCta: true,
      ctaLabel: 'Register Interest',
      ctaHref: '#early-access',
    },
  },
  {
    id: 'demo-video',
    eyebrow: 'Watch it Work',
    headline: 'Click the preview and access a guided tour in 90 seconds to see Aana in action',
    description: '',
    bullets: [],
    primaryCta: null,
    secondaryCta: null,
    visualPosition: 'right',
    visualCaption: {
      text: 'Looping preview from the current product demo.',
      showCta: true,
      ctaLabel: 'Register Interest',
      ctaHref: '#early-access',
    },
  },
];

const FeatureShowcase = () => {
  return (
    <section id="features" className="feature-showcase">
      {/* Section header (optional - can be removed if you want panels to speak for themselves) */}
      <motion.div
        className="feature-showcase__header"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-50px' }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <h2 className="feature-showcase__title">
          Everything you need to learn, teach, and connect.
        </h2>
      </motion.div>

      {/* Feature panels */}
      <div className="feature-showcase__panels">
        {FEATURE_PANELS.map((panel) => (
          <FeaturePanel
            key={panel.id}
            id={panel.id}
            eyebrow={panel.eyebrow}
            headline={panel.headline}
            description={panel.description}
            bullets={panel.bullets}
            tagline={panel.tagline}
            primaryCta={panel.primaryCta}
            secondaryCta={panel.secondaryCta}
            visualPosition={panel.visualPosition}
            visualPlaceholder={false}
            visualContent={
              panel.id === 'workspace'
                ? <ImageCarousel />
                : panel.id === 'content'
                  ? <CollageVisual />
                  : <LoopVideoVisual />
            }
            visualCaption={panel.visualCaption}
          />
        ))}
      </div>
    </section>
  );
};

export default FeatureShowcase;
